import { useEffect, useMemo, useState } from "react";
import { Box, Button } from "@mui/material";
import { ApiDataProps } from "../types";
import { IntegrationFieldConfig } from "../../../services/types";
import AddIcon from "@mui/icons-material/Add";
import { useTranslation } from "react-i18next";
import ItemSelectionDialog from "./ItemSelectionDialog";
import SelectedListItems from "./SelectedListItems";
import { get } from "lodash";
import useIntegrationField from "../useIntegrationField";
import DndContextProvider from "shell/components/DndContextProvider";
type IntegrationFieldSelectProps = {
  name: string;
  label: string;
  maxItems?: number;
  value: ApiDataProps[];
  config: IntegrationFieldConfig;
  onChange: (value: ApiDataProps[]) => void;
};
const IntegrationFieldSelect = ({
  name,
  label,
  maxItems,
  value,
  config,
  onChange
}: IntegrationFieldSelectProps) => {
  const {
    t
  } = useTranslation();
  const {
    data: apiData,
    status,
    fetchApiData
  } = useIntegrationField();
  const [open, setOpen] = useState(false);
  const [selectedItems, setSelectedItems] = useState<ApiDataProps[]>((Array.isArray(value) ? value : []).map(item => ({
    ...item,
    _itemId: get(item, config?.keyPaths?.itemId)
  })));
  const isError = status === "failed";
  const isLoading = status === "connecting";
  const launchSelector = () => {
    const options = !config?.headers ? {} : {
      headers: config?.headers
    };
    fetchApiData(config?.endpoint, options);
    setOpen(true);
  };
  const handleSave = (items: ApiDataProps[]) => {
    onChange(items?.map(item => {
      const {
        _itemId,
        ...restItems
      } = item;
      return restItems;
    }));
  };
  const items: ApiDataProps[] = useMemo(() => {
    if (isLoading || !apiData || isError) return [];
    const data = (!config?.keyPaths?.rootPath ? apiData : get(apiData, config?.keyPaths?.rootPath)) || [];
    const itemWithId = !data?.length ? [] : data?.map((item: ApiDataProps) => ({
      ...item,
      _itemId: get(item, config?.keyPaths?.itemId)
    }));
    return itemWithId;
  }, [apiData, isError, isLoading, config?.keyPaths]);
  useEffect(() => {
    const newValue = (Array.isArray(value) ? value : []).map((item, index) => {
      // The user has reconfigured the API, and the data structure has been updated.
      // A unique ID should be assigned to each item so that users can still delete individual items manually.
      // These items will not appear in the selection dialog and must be removed through manual deletion.
      const itemId = get(item, config?.keyPaths?.itemId) || `unresolved-${index}`;
      return {
        ...item,
        _itemId: itemId
      };
    });
    setSelectedItems(newValue);
  }, [value, setSelectedItems]);
  return <Box width="100%" data-sentry-element="Box" data-sentry-component="IntegrationFieldSelect" data-sentry-source-file="index.tsx">
      <DndContextProvider data-sentry-element="DndContextProvider" data-sentry-source-file="index.tsx">
        <SelectedListItems items={selectedItems} config={config} onChange={handleSave} data-sentry-element="SelectedListItems" data-sentry-source-file="index.tsx" />
      </DndContextProvider>

      <Button data-cy="integrationSelectItemsButton" variant="outlined" color="primary" size="large" fullWidth={true} startIcon={<AddIcon />} onClick={launchSelector} data-sentry-element="Button" data-sentry-source-file="index.tsx">
        {t("shell.integrationSelectRemoteItems")}
      </Button>
      {open && <ItemSelectionDialog title={label} maxItems={maxItems} loading={isLoading} items={items} open={open} onClose={() => setOpen(false)} value={selectedItems} config={config} onSave={handleSave} />}
    </Box>;
};
export default IntegrationFieldSelect;